import React, { Fragment, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import compose from 'lodash.flowright';

import {
  PHOTO_SEARCH_CHECKOUT_SUCCESS_MODAL,
  PhotoSearchCheckoutSuccessModal,
} from '../PhotoSearchCheckout/components/PhotoSearchCheckoutSuccess';
import Button from '../../../../../components/global/Button';
import useCloseModal from '../../../../../hooks/useCloseModal';
import useOpenAlert from '../../../../../hooks/useOpenAlert';
import { AlbumFolderWithCreatorDetails } from '../../../../../graphql/queries/album-folder';
import Spinner from '../../../../../components/global/Spinner';
import { AccountPhotoSearchCheckout } from '../../../../../graphql/queries/account';
import { canSkipCheckout } from '../../../../../util/account';
import usePhotoSearchCheckout from '../PhotoSearchCheckout/hooks/usePhotoSearchCheckout';
import WaldoSpeechBubble from '../../../../../components/global/WaldoSpeechBubble';
import { getAsset } from '../../../../../util/assets';
import { ALERT_MODAL } from '../../../../../constants/modals';
import PhotoSearchUpsell from './PhotoSearchUpsell';
import OnboardingLayoutContentLarge from '../OnboardingLayout/components/OnboardingLayoutContentLarge';
import { ALBUM_SUBTYPE } from '../../../../../constants/albums';

const PhotoSearchUpsellCheckout = ({
  history,
  account,
  albumFolder,
  loading,
  onSuccess,
}) => {
  const closeModal = useCloseModal();
  const openAlert = useOpenAlert();
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { onCheckout } = usePhotoSearchCheckout({ account });

  const onCheckoutForced = async () => {
    setSubmitting(true);
    try {
      await onCheckout();

      if (onSuccess) {
        onSuccess();
      } else {
        history.replace(`/purchase/album-folder/${albumFolder.id}/success`);
        //   If we fail open the modal and let it handle things.
      }
    } catch (err) {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!account || !albumFolder) {
      return;
    }

    // there's no need to open the modal at all if the user can skip
    // the checkout (has sub) since it just leads to the upsell
    if (canSkipCheckout(account)) {
      onCheckoutForced();
    }
  }, [account, albumFolder]);

  useEffect(() => {
    if (success && onSuccess) {
      onSuccess();
    }
  }, [success]);

  if (loading || !albumFolder || !account || submitting) {
    return (
      <div>
        <Spinner />
      </div>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>Buy photo searches &ndash; Waldo Photos</title>
      </Helmet>
      {!success && (
        <OnboardingLayoutContentLarge>
          <WaldoSpeechBubble className="w-100-i mb-2" position="right">
            Pick a plan to receive all your matched photos and share them with
            family!
          </WaldoSpeechBubble>
          <p className="text-light">
            Enjoy automated photo delivery during{' '}
            {albumFolder?.album_subtype === ALBUM_SUBTYPE.CAMPS
              ? 'camp'
              : 'this event'}
            , and then create your own events for private and effortless photo
            &amp; video sharing all year long!
          </p>
          <div className="onboarding-photo-search-upsell">
            <PhotoSearchUpsell
              dismissible={false}
              alertOnSuccess={false}
              freePlanHidden
              albumFolder={albumFolder}
              renderHeader={() => null}
              onCheckoutSuccess={({ success: checkoutSuccess } = {}) => {
                closeModal(PHOTO_SEARCH_CHECKOUT_SUCCESS_MODAL);
                setSuccess(checkoutSuccess ?? false);

                if (!checkoutSuccess) {
                  openAlert({
                    type: 'info',
                    message: (
                      <Fragment>
                        <p>
                          Please note that a Plus subscription is required to
                          join this gallery.
                        </p>
                        <Button
                          btnStyle="link"
                          btnBlock
                          onClick={() => {
                            closeModal(ALERT_MODAL);
                            history.goBack();
                          }}
                        >
                          Back to summary
                        </Button>
                      </Fragment>
                    ),
                  });
                }
              }}
            />
          </div>
        </OnboardingLayoutContentLarge>
      )}

      {success && !onSuccess && (
        <div className="d-flex flex-column w-100 flex-auto">
          <WaldoSpeechBubble position="right" iconPath="/waldo-excited.png">
            Congrats on becoming a Waldo member and joining{' '}
            <span className="text-primary">{albumFolder?.name}</span> !
          </WaldoSpeechBubble>
          <div className="purchase-success-container">
            <p className="text-center">
              As soon as photos are matched, you will receive a text
              notification. Meanwhile, you can check the event gallery below
            </p>
            <div className="d-flex align-items-center flex-column justify-content-center mt-1">
              <Button
                onClick={() =>
                  history.push(
                    `/app/shutterbug/hierarchy-items/${albumFolder.id}`,
                  )
                }
                className="px-6"
              >
                Visit the event gallery
              </Button>
            </div>
            <div className="text-center text-xs mt-auto">
              <div className="text-center text-xs mt-auto">
                <span className="text-dark text-extra-bold">Note:</span> Waldo
                turns off photo alerts between 11 p.m. and 7 a.m. so he
                won&rsquo;t disrupt your evening!
              </div>
              <div className="text-center mt-2">
                <div className="purchase-success-social-icons">
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.facebook.com/waldophotos"
                  >
                    <img
                      alt="Facebook Page"
                      src={getAsset('/icon-facebook.png')}
                      width={25}
                    />
                  </a>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.instagram.com/waldophotos"
                  >
                    <img
                      alt="Instagram Page"
                      src={getAsset('/icon-instagram.png')}
                      width={25}
                    />
                  </a>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://twitter.com/waldophotos"
                  >
                    <img
                      alt="Twitter Page"
                      src={getAsset('/icon-twitter.png')}
                      width={25}
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <PhotoSearchCheckoutSuccessModal />
    </Fragment>
  );
};

export { PhotoSearchUpsellCheckout as PhotoSearchUpsellComponentCheckout };

const withAccount = graphql(AccountPhotoSearchCheckout, {
  props: ({ data: { me } = {} }) => ({
    account: me,
  }),
  options: {
    fetchPolicy: 'cache-and-network',
  },
});

const withAlbumFolderData = graphql(AlbumFolderWithCreatorDetails, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumFolderId,
    },
  }),
  props: ({ data: { albumFolder, loading } }) => ({
    albumFolder,
    loadingAlbumFolder: loading,
  }),
});

export default compose(
  withRouter,
  withAccount,
  withAlbumFolderData,
)(PhotoSearchUpsellCheckout);
